'use client';

import {
	ChakraProvider,
	createSystem,
	defaultConfig,
	defineConfig,
	defineLayerStyles,
	defineRecipe,
	defineTextStyles,
} from '@chakra-ui/react';
import { dialogAnatomy } from '@chakra-ui/react/anatomy';

import { ColorModeProvider, type ColorModeProviderProps } from './color-mode';
import { CONTROL_SURFACE_SHADOW } from './shadows';

/**
 * Typography vocabulary — size + weight + line-height defined once per intent, so call sites state
 * intent (`textStyle="body"`) instead of repeating triplets.
 *
 * Sizes are scale TOKENS, never px. The app does not override Chakra's font scale, so `md` is 16px
 * and `moderate` is 1.5 — writing the token instead of the literal renders identically while making
 * a migration definitional: a `fontSize="md"` site becomes `textStyle="body"` because `body` IS
 * `md`, not because someone judged it to be close enough.
 *
 * `moderate` (1.5) is a RATIO, and so is what an element inherits when it states no line-height, so
 * adopting a text style no longer changes the line box. That is recent: Tailwind's `text-sm` on
 * `body` set line-height as a LENGTH (1.25rem), so every element got a 20px box whatever its font
 * size, and a pixel diff of a type migration showed that shift rather than the type change. If a
 * length ever returns to `body`, this vocabulary silently stops being free to adopt.
 *
 * Headings are not here. A heading is a `<Title>` — an element with an outline entry, not a weight
 * applied to a paragraph — and its levels are named for a role so they cannot be mistaken for the
 * scale words the way `heading.md` (20px) could be mistaken for `fontSize="md"` (16px).
 */
const textStyles = defineTextStyles({
	body: {
		description: 'Default body copy',
		value: { fontSize: 'md', fontWeight: 'normal', lineHeight: 'moderate' },
	},
	'body.strong': {
		description:
			'Body copy that carries weight without being a heading — a value beside its label, a row’s lead-in',
		value: { fontSize: 'md', fontWeight: 'semibold', lineHeight: 'moderate' },
	},
	'body.sm': {
		description: 'Body copy one step below the 16px base — the app’s most common size by call count',
		value: { fontSize: 'sm', fontWeight: 'normal', lineHeight: 'moderate' },
	},
	'label.lg': {
		description: 'A row label with a description beneath it — label one step up',
		value: { fontSize: 'md', fontWeight: 'medium', lineHeight: 'moderate' },
	},
	label: {
		description: 'Form and field labels — body.sm weighted up',
		value: { fontSize: 'sm', fontWeight: 'medium', lineHeight: 'moderate' },
	},
	'label.sm': {
		description: 'The smallest label — a caption on a control, a column header',
		value: { fontSize: 'xs', fontWeight: 'medium', lineHeight: 'moderate' },
	},
	micro: {
		description: 'Metadata and tiny labels — the smallest step in the ramp',
		value: { fontSize: 'xs', fontWeight: 'normal', lineHeight: 'moderate' },
	},
	'micro.strong': {
		description: 'An emphasised metric in a chip — a count, a progress figure',
		value: { fontSize: 'xs', fontWeight: 'semibold', lineHeight: 'moderate' },
	},

	/**
	 * Scale vocabulary — size and leading only. Weight stays a separate prop.
	 *
	 * The role styles above name a size AND a weight together, so every combination needs its own
	 * name: three sizes by three weights is nine cells, and eight of them are spelled out above with
	 * 14px/600 simply missing. These name the size and stop, because weight is an independent axis —
	 * `<Text textStyle="text.sm" fontWeight="semibold">` reaches the missing cell without a tenth name.
	 *
	 * Leading is paired per step rather than left at the `moderate` ratio, so a step carries its own
	 * line box. This is NOT the Tailwind bug: that applied ONE length to every element whatever its
	 * size, giving 18px text a 20px box. Here each length belongs to the size it ships with.
	 */
	'text.xs': {
		description: '12px — metadata, captions, column headers',
		value: { fontFamily: 'body', fontSize: 'xs', lineHeight: 'xs', letterSpacing: 'normal' },
	},
	'text.sm': {
		description: '14px — secondary copy',
		value: { fontFamily: 'body', fontSize: 'sm', lineHeight: 'sm', letterSpacing: 'normal' },
	},
	'text.md': {
		description: '16px — the anchor step, and the app’s base size',
		value: { fontFamily: 'body', fontSize: 'md', lineHeight: 'md', letterSpacing: 'normal' },
	},
	'text.lg': {
		description: '18px',
		value: { fontFamily: 'body', fontSize: 'lg', lineHeight: 'lg', letterSpacing: 'normal' },
	},
	'text.xl': {
		description: '20px',
		value: { fontFamily: 'body', fontSize: 'xl', lineHeight: 'xl', letterSpacing: 'normal' },
	},

	/**
	 * Display — 24px and up, tracked in at -0.02em because type set this large reads loose at 0.
	 * Nothing above `text.xl` had a home before, so large headings improvised with bare scale words.
	 */
	'display.xs': {
		description: '24px',
		value: { fontFamily: 'heading', fontSize: 'display-xs', lineHeight: 'display-xs', letterSpacing: 'display' },
	},
	'display.sm': {
		description: '38px',
		value: { fontFamily: 'heading', fontSize: 'display-sm', lineHeight: 'display-sm', letterSpacing: 'display' },
	},
	'display.md': {
		description: '44px',
		value: { fontFamily: 'heading', fontSize: 'display-md', lineHeight: 'display-md', letterSpacing: 'display' },
	},
	'display.lg': {
		description: '60px',
		value: { fontFamily: 'heading', fontSize: 'display-lg', lineHeight: 'display-lg', letterSpacing: 'display' },
	},
	'display.xl': {
		description: '72px',
		value: { fontFamily: 'heading', fontSize: 'display-xl', lineHeight: 'display-xl', letterSpacing: 'display' },
	},
	'display.2xl': {
		description: '90px',
		value: { fontFamily: 'heading', fontSize: 'display-2xl', lineHeight: 'display-2xl', letterSpacing: 'display' },
	},
});

/**
 * Surfaces — the bg + radius + border + shadow quartet that 41 bordered panels repeat by hand.
 *
 * Layer styles rather than a slot recipe or a component, because a surface is not one element here:
 * it is a Box, a VStack, a Dialog.Content, a Card.Root, a Menu.Item. A component could only wrap the
 * Box case; `layerStyle` composes onto whatever the surface already is.
 *
 * `2xl` (16px) is the radius, chosen as the majority spelling among panels that already exist. The
 * app currently draws a card at 16, 24, 12, 6 and 4px plus one off-scale 30px, so adopting this
 * normalises the other five toward the most common.
 *
 * Elevation is the axis that actually varies: flat in the page flow, raised over it, or inset into
 * it. A surface wanting a different background or border is not one of these and stays explicit.
 */
const layerStyles = defineLayerStyles({
	card: {
		description: 'Bordered panel sitting in the page flow',
		value: {
			bg: 'surface.default',
			borderRadius: '2xl',
			borderWidth: '1px',
			borderColor: 'border.default',
			boxShadow: 'xs',
		},
	},
	'card.raised': {
		description: 'Panel floating above the page — menus, popovers, dropdowns',
		value: {
			bg: 'surface.default',
			borderRadius: '2xl',
			borderWidth: '1px',
			borderColor: 'border.default',
			boxShadow: 'lg',
		},
	},
	'card.inset': {
		description: 'Recessed panel — a well within a card, not a card of its own',
		value: {
			bg: 'surface.subtle',
			borderRadius: '2xl',
		},
	},
	keyCap: {
		description: 'A keyboard key, drawn as the key it names — the caps in a shortcut hint',
		value: {
			bg: 'surface.default',
			borderRadius: 'sm',
			borderWidth: '1px',
			borderColor: 'border.default',
			// A single hard pixel under the cap rather than a blur: at 16px a soft shadow is mud, and
			// the one line is what reads as the key having a lip.
			boxShadow: '0 1px 0 {colors.border.default}',
			// Between `fg.muted` and `fg.subtle`, neither of which is right: muted competes with the
			// word beside it and subtle is two steps too pale to sit on a white cap.
			color: '{colors.brand.500}',
		},
	},
	control: {
		description: 'Pill-shaped strip holding controls — a view toggle, a filter group, an action bar',
		value: {
			bg: 'surface.default',
			borderRadius: 'full',
			borderWidth: '1px',
			borderColor: 'border.default',
			boxShadow: 'xs',
		},
	},
});

/**
 * Button visuals — one definition per intent, replacing the PrimaryButton → SecondaryButton →
 * OutlinedButton → CancelOutlinedButton wrapper chain. Opt-in via `<Button visual="…">`: the recipe
 * has no `base` and no `defaultVariants`, because Chakra merges those into every `<Button>`
 * app-wide — raw buttons stay untouched until each is audited.
 *
 * Each variant is derived from the wrapper it replaces, and the wrappers move: upstream retuned
 * DangerButton from bold/md to medium/sm and gave OutlinedButton an `elevated` prop while this
 * recipe sat unused, so it encoded values that had already been superseded. Re-derive from the
 * wrapper source, never from this comment, until the wrappers are gone.
 *
 * Three escapes are mapped to tokens rather than copied: primary's #233845 hover hex to brand.800
 * (1/255 off per channel), cancel's literal `white` to surface.default so it follows dark mode, and
 * danger's raw `red`/`30px` to red.solid/full.
 */
const buttonRecipe = defineRecipe({
	variants: {
		visual: {
			// TODO: chakra-migration design — bg `primary` resolves to light grey in dark mode with white
			// text; brand.solid/brand.contrast (yellow + dark text) is likely the intended dark rendering
			primary: {
				/**
				 * PrimaryButton's layout defaults live here, not at the call site. A recipe's `base`
				 * would reach every `<Button>`, but a variant only reaches what opts into it — which is
				 * precisely the set that used the wrapper. Sites wanting an auto-width primary override
				 * with `w="auto"`, exactly as they already did against the wrapper.
				 */
				width: '100%',
				py: 2,
				bg: 'primary',
				color: 'white',
				fontSize: 'sm',
				fontWeight: 'medium',
				borderRadius: 'full',
				_hover: { bg: 'brand.800', color: 'white' },
				_disabled: { cursor: 'default', opacity: 0.5 },
			},
			// TODO: chakra-migration design — `secondary` is brand yellow in both modes while `primary`
			// text goes light grey in dark, leaving grey on yellow; the pair needs a dark-mode decision
			secondary: {
				bg: 'secondary',
				color: 'primary',
				fontSize: 'md',
				fontWeight: 'medium',
				borderRadius: 'full',
				_hover: { bg: 'surface.subtle' },
				_disabled: { cursor: 'default', opacity: 0.5 },
			},
			outline: {
				bg: 'surface.default',
				color: 'primary',
				fontSize: 'sm',
				fontWeight: 'medium',
				borderWidth: '1px',
				borderColor: 'border.default',
				borderRadius: 'full',
				minW: '96px',
				cursor: 'pointer',
				_hover: { bg: 'surface.subtle' },
				// Pressed and held-open both fall through to Chakra's own Button without these, which
				// darkens to near black — a colour this recipe uses nowhere.
				//
				// The press used to take a slight scale with it. Held out for now: it is on every
				// outlined button in the app, and on the wider ones it reads as the button sinking
				// rather than as answering the finger.
				_active: { bg: 'surface.subtle' /*, transform: 'scale(0.97)' */ },
				_expanded: { bg: 'surface.subtle' },
				_disabled: { cursor: 'default', opacity: 0.5 },
			},
			dangerOutline: {
				bg: 'surface.default',
				color: 'red.600',
				fontSize: 'sm',
				fontWeight: 'medium',
				borderWidth: '1px',
				borderColor: 'red.300',
				borderRadius: 'full',
				minW: '96px',
				cursor: 'pointer',
				_hover: { bg: 'red.500', color: 'white' },
				// Without this the press falls through to Chakra's own Button, which darkens to near
				// black — a colour this recipe uses nowhere and which reads as a different control.
				_active: { bg: 'red.600', color: 'white' },
				_disabled: { cursor: 'default', opacity: 0.5 },
			},
			/**
			 * No call site constrains this one — DangerButton had none — so it is a fresh definition
			 * rather than a port. Its wrapper hovered to `primary/12`, a translucent blue on a red
			 * button, which reads as an accident of inheriting SecondaryButton rather than a choice.
			 */
			danger: {
				bg: 'red.solid',
				color: 'white',
				fontSize: 'sm',
				fontWeight: 'medium',
				borderRadius: 'full',
				// `red.solid` already resolves to `red.600`, so hovering to it painted the colour the
				// button was already wearing. One step either side of the base instead — and hovering
				// lighter is the direction `dangerOutline` already goes, which fills to the same red.
				_hover: { bg: 'red.500' },
				_active: { bg: 'red.700' },
				_disabled: { cursor: 'default', opacity: 0.5 },
			},
		},
		/**
		 * OutlinedButton's `elevated` prop, kept as its own axis so it composes with any visual instead
		 * of doubling the variant list. It keeps the wrapper's own name so a site migrating off
		 * OutlinedButton carries the prop across unchanged, and it matches the card layer style's
		 * vocabulary for the same raised/flat distinction.
		 */
		elevated: {
			true: { boxShadow: CONTROL_SURFACE_SHADOW },
		},
		/** Three verdicts share the mobile row, then return to the standard button recipe on desktop. */
		verdictRow: {
			true: {
				minW: { base: 0, md: '96px' },
				px: { base: 2, md: 3.5 },
			},
		},
	},
});

export const brandColors = {
	50: '#f7f8f8',
	100: '#eaecee',
	200: '#d5dadd',
	300: '#b9c1c7',
	400: '#a2adb4',
	500: '#8b98a1',
	600: '#6b7c87',
	700: '#2c4454',
	800: '#243946',
	900: '#1d2e38',
	950: '#15232b',
} as const;

export const primaryButtonColor = '#2c4454';
const brandYellow = '#FFF700';
const yellowColors = {
	50: '#fffef0',
	100: '#fffbc2',
	200: '#fff78a',
	300: '#fff34d',
	400: '#ffef1a',
	500: '#FFF700',
	600: '#d6d000',
	700: '#ada900',
	800: '#847f00',
	900: '#5c5800',
	950: '#373500',
} as const;
export const brandBlue = brandColors[700];
const darkBackground = '#1a1a1a';
const darkText = '#e5e5e5';
const darkSidebar = '#2C2C2C';
const darkBorder = '#353535';
const darkPlaceholder = '#767676';

const config = defineConfig({
	globalCss: {
		'html, body, #root': {
			bg: 'surface.canvas',
		},
		/**
		 * The base size is 16px — `md`, the browser default, and the step the scale is anchored on.
		 *
		 * It was 14px until this line was removed, carried over from Tailwind's `@apply text-sm` on
		 * body long after Tailwind itself went. That made `md` an anchor the app never actually sat
		 * on: text stating no size rendered a step below the token every scale reads from.
		 *
		 * Nothing states 16px here because nothing needs to — `html` already resolves to it. Sites
		 * that genuinely want 14px say so, and 120 `fontSize="sm"` plus 139 `textStyle="body.sm"`
		 * already do; this only moves text that never stated a size at all.
		 *
		 * TODO(type-scale): 150 of 677 `<Text>` state no size and so moved 14px -> 16px with this.
		 * They are not a random cross-section: most state a colour or a weight and nothing else —
		 * `fg.muted`, `fg.subtle`, `text.error` — which is copy that was leaning on the old base to
		 * read as secondary. Some of the 150 are right at 16px and some are not; each needs a look
		 * rather than a sweep.
		 *
		 * Colour is deliberately absent: `html { color: fg }` supplies it, which is the point of
		 * overriding `fg` below.
		 */
		'#storybook-root': {
			minH: '100vh',
			bg: 'surface.canvas',
		},
	},
	theme: {
		textStyles,
		layerStyles,
		slotRecipes: {
			// Chakra's dialog positioner has no padding and its content is `width: 100%`, so a dialog
			// sized only by maxW sits flush to both screen edges on any viewport under that cap. The
			// gutter is mobile-only: above `md` every dialog is already narrower than the viewport and
			// centred, so padding there would shift long-settled desktop layouts. `full` opts out —
			// a full-bleed sheet wants the edges.
			dialog: {
				slots: dialogAnatomy.keys(),
				base: {
					positioner: { padding: { base: 4, md: 0 } },
				},
				variants: {
					size: {
						full: { positioner: { padding: 0 } },
					},
				},
			},
		},
		recipes: {
			button: buttonRecipe,
			input: {
				variants: {
					size: {
						md: { fontSize: 'md' },
					},
				},
			},
		},
		tokens: {
			fonts: {
				// Reserved for words a person wrote themselves, so their own note reads as theirs rather
				// than as another field the app filled in. Never for app-authored copy.
				handwriting: { value: "'Patrick Hand', 'Segoe Script', 'Bradley Hand', cursive" },
			},
			// Only the display steps are defined here. xs–xl already resolve to 12/14/16/18/20px in
			// Chakra's defaults, so restating them would be a copy that can drift.
			fontSizes: {
				'display-xs': { value: '1.5rem' },
				'display-sm': { value: '2.375rem' },
				'display-md': { value: '2.75rem' },
				'display-lg': { value: '3.75rem' },
				'display-xl': { value: '4.5rem' },
				'display-2xl': { value: '5.625rem' },
			},
			// Chakra's own line-heights are ratios named for tightness (`moderate`, `tall`). These are
			// lengths named for the size step they belong to, so `text.sm` can pair 14px with an 18px
			// box. Both sets coexist: the role styles above still use `moderate`.
			lineHeights: {
				xs: { value: '1.125rem' },
				sm: { value: '1.25rem' },
				md: { value: '1.5rem' },
				lg: { value: '1.75rem' },
				xl: { value: '1.875rem' },
				'display-xs': { value: '2rem' },
				'display-sm': { value: '2.375rem' },
				'display-md': { value: '2.75rem' },
				'display-lg': { value: '3.75rem' },
				'display-xl': { value: '4.5rem' },
				'display-2xl': { value: '5.625rem' },
			},
			letterSpacings: {
				display: { value: '-0.02em' },
			},
			colors: {
				brand: {
					50: { value: brandColors[50] },
					100: { value: brandColors[100] },
					200: { value: brandColors[200] },
					300: { value: brandColors[300] },
					400: { value: brandColors[400] },
					500: { value: brandColors[500] },
					600: { value: brandColors[600] },
					700: { value: brandColors[700] },
					800: { value: brandColors[800] },
					900: { value: brandColors[900] },
					950: { value: brandColors[950] },
				},
				yellow: {
					50: { value: yellowColors[50] },
					100: { value: yellowColors[100] },
					200: { value: yellowColors[200] },
					300: { value: yellowColors[300] },
					400: { value: yellowColors[400] },
					500: { value: yellowColors[500] },
					600: { value: yellowColors[600] },
					700: { value: yellowColors[700] },
					800: { value: yellowColors[800] },
					900: { value: yellowColors[900] },
					950: { value: yellowColors[950] },
				},
			},
		},
		semanticTokens: {
			colors: {
				brand: {
					solid: {
						value: { base: '{colors.brand.700}', _dark: '{colors.yellow.500}' },
					},
					solidHover: {
						value: { base: '{colors.brand.900}', _dark: '{colors.yellow.600}' },
					},
					contrast: {
						value: { base: 'white', _dark: darkBackground },
					},
					/**
					 * The auth screens' foreground on `brand.solid` — brand yellow on the blue button,
					 * rather than the white `contrast` gives the other 22 solid surfaces.
					 *
					 * It exists because `secondary` could not do this job: that alias is #FFF700 in BOTH
					 * modes, while `brand.solid` turns yellow in dark, so the pair rendered #FFF700 text
					 * on a #FFF700 button — invisible on the primary sign-in CTA. Dark takes the page
					 * background instead, which is the same inversion `contrast` makes.
					 */
					onSolid: {
						value: { base: '{colors.yellow.500}', _dark: darkBackground },
					},
					fg: {
						value: { base: '{colors.brand.700}', _dark: '{colors.yellow.500}' },
					},
					muted: {
						value: { base: '{colors.brand.100}', _dark: '{colors.yellow.100}' },
					},
					subtle: {
						value: { base: '{colors.brand.50}', _dark: '{colors.yellow.50}' },
					},
					emphasized: {
						value: { base: '{colors.brand.200}', _dark: '{colors.yellow.200}' },
					},
					focusRing: {
						value: { base: '{colors.brand.500}', _dark: brandYellow },
					},
				},
				// Legacy compatibility aliases. Prefer brand.* for fixed brand colors
				// and semantic tokens like surface.*, border.*, sidebar.* (may still adjust text) for new work.
				primary: {
					value: { base: '{colors.brand.700}', _dark: darkText },
				},
				'primary.fixed': {
					value: { base: '{colors.brand.700}', _dark: '{colors.brand.700}' },
				},
				secondary: {
					value: { base: brandYellow, _dark: brandYellow },
				},
				'secondary.fixed': {
					value: { base: brandYellow, _dark: brandYellow },
				},
				/**
				 * Foreground — Chakra's own vocabulary, given this app's values rather than a parallel
				 * `text.*` family beside it.
				 *
				 * Every Chakra component styles itself with `fg` and `fg.muted`, so leaving them at the
				 * defaults while app-authored text used brand values meant two colour systems in one
				 * screen: built-in text rendered pure black next to brand.800.
				 *
				 * Overriding `fg` also reaches the `html { color: fg }` rule Chakra ships, which is what
				 * lets a call site say nothing at all and still be right. The most common colour in the
				 * app is now the one nobody has to write.
				 *
				 * USE `_light`, NOT `base`, WHEN THE NAME IS ONE CHAKRA ALREADY SHIPS. Chakra's preset
				 * defines `fg`, `bg` and `border` under the `_light` condition, and a `_light`
				 * declaration outranks an unconditional `base` one — so `base` compiles, type-checks
				 * and loses, leaving the app on Chakra's greys while this file reads as if it were
				 * branded. Measured by setting the values to pure blue: `base` reached 0 elements,
				 * `_light` reached 1458. Names this app invents (`surface.*`, `primary`) have nothing
				 * to lose to and work either way, which is what made this so quiet.
				 *
				 * The nesting is Chakra's own shape — see its `theme/semantic-tokens/colors.js`. A flat
				 * `'fg.muted'` key also renders, but it mints a parallel token rather than overriding
				 * the real one, so Chakra's internal `fg` references keep the default (809 elements,
				 * not 1458). Match the preset's shape.
				 */
				fg: {
					DEFAULT: { value: { _light: '{colors.brand.700}', _dark: darkText } },
					// TODO: chakra-migration design — brand.600 measures 4.32:1 on white, under the 4.5:1 WCAG AA
					// floor for normal text, and it is the app's most-used non-default colour (344 call sites).
					// It is the value this branch declared, so it ships; #677782 is the same hue at 4.63:1 if the
					// floor has to be met.
					muted: { value: { _light: '{colors.brand.600}', _dark: darkPlaceholder } },
					subtle: { value: { _light: '{colors.brand.300}', _dark: darkPlaceholder } },
				},
				text: {
					error: {
						value: { base: '{colors.red.500}', _dark: '{colors.red.400}' },
					},
					onPrimary: {
						value: { base: 'white', _dark: 'white' },
					},
				},
				border: {
					default: {
						value: { base: '{colors.brand.100}', _dark: darkBorder },
					},
					subtle: {
						value: { _light: '{colors.brand.50}', _dark: darkBorder },
					},
					strong: {
						value: { base: '{colors.brand.300}', _dark: darkBorder },
					},
				},
				surface: {
					default: {
						value: { base: 'white', _dark: darkBackground },
					},
					canvas: {
						value: { base: '#fcfcfd', _dark: darkBackground },
					},
					subtle: {
						value: { base: '{colors.brand.50}', _dark: darkSidebar },
					},
				},
				sidebar: {
					surface: {
						value: { base: '{colors.brand.50}', _dark: darkSidebar },
					},
					border: {
						value: { base: '{colors.brand.100}', _dark: darkBorder },
					},
					hover: {
						value: { base: 'white', _dark: 'rgba(255, 210, 135, 0.08)' },
					},
					active: {
						value: { base: '{colors.brand.100}', _dark: 'rgba(255, 210, 135, 0.104)' },
					},
					activeHover: {
						value: { base: 'white', _dark: 'rgba(255, 210, 135, 0.16)' },
					},
				},
				// Dark-mode values use the "house lights" warm-tint palette
				// (warmth=100, brightness=40 from the animation example) so the
				// shutter pill in dark mode glows like a softly lit lamp instead
				// of swapping a cool grey background.
				focus: {
					ring: {
						value: { base: '{colors.brand.400}', _dark: brandYellow },
					},
				},
				'accent.foreground': {
					value: { base: darkBackground, _dark: darkBackground },
				},
			},
		},
	},
});

export const system = createSystem(defaultConfig, config);

export function Provider(props: ColorModeProviderProps) {
	return (
		<ChakraProvider value={system}>
			<ColorModeProvider
				{...props}
				defaultTheme="light"
				enableSystem={false} // Don't use system preference
				storageKey="my-bundle-ui-color-mode"
			/>
		</ChakraProvider>
	);
}
